import { useEffect, useRef } from 'react';

export const useHorizontalRowScroll = <T extends HTMLElement>() => {
  const ref = useRef<T | null>(null);

  // Wheel: map vertical delta to horizontal when needed (trackpads still use deltaX)
  useEffect(() => {
    const el = ref.current;
    if (!el) {
      return;
    }

    const onWheel = (e: WheelEvent) => {
      if (e.ctrlKey || el.scrollWidth <= el.clientWidth) {
        return;
      }

      // prefer deltaX (trackpads), else map deltaY → horizontal
      let delta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;

      // normalize Firefox "line" units to px
      if (e.deltaMode === 1) {
        delta *= 16;
      }

      if (!delta) {
        return;
      }

      const before = el.scrollLeft;
      el.scrollLeft += delta;

      // prevent page scroll only if this element consumed the wheel
      if (el.scrollLeft !== before) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel as EventListener);
  }, []);

  // Touch swipe: horizontal only, do not catch mouse drag
  useEffect(() => {
    const el = ref.current;
    if (!el) {
      return;
    }

    let active = false;
    let startX = 0;
    let startY = 0;
    let startLeft = 0;
    let horizontalIntent: boolean | null = null; // null until decided
    const INTENT_THRESHOLD = 6; // px

    const onTouchStart = (e: TouchEvent) => {
      if (el.scrollWidth <= el.clientWidth) {
        return;
      }
      if (e.touches.length !== 1) {
        return;
      } // ignore multi-touch (pinch, etc.)

      active = true;
      horizontalIntent = null;
      const t = e.touches[0];
      startX = t.clientX;
      startY = t.clientY;
      startLeft = el.scrollLeft;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!active || e.touches.length !== 1) {
        return;
      }

      const t = e.touches[0];
      const dx = t.clientX - startX;
      const dy = t.clientY - startY;

      // decide intent once
      if (horizontalIntent === null) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) < INTENT_THRESHOLD) {
          return;
        }
        horizontalIntent = Math.abs(dx) > Math.abs(dy); // true → horizontal
      }

      if (!horizontalIntent) {
        // vertical scroll: let the page scroll naturally
        return;
      }

      // Horizontal swipe: move scrollLeft opposite to finger
      const prev = el.scrollLeft;
      el.scrollLeft = startLeft - dx;

      // prevent page scrolling while swiping horizontally, but only if we scrolled
      if (el.scrollLeft !== prev) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const end = () => {
      active = false;
      horizontalIntent = null;
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', end);
    el.addEventListener('touchcancel', end);

    return () => {
      el.removeEventListener('touchstart', onTouchStart as EventListener);
      el.removeEventListener('touchmove', onTouchMove as EventListener);
      el.removeEventListener('touchend', end as EventListener);
      el.removeEventListener('touchcancel', end as EventListener);
    };
  }, []);

  return ref;
};
